import { Nullable } from 'common/models/optional';
import { StorageUtils } from 'utils/storageUtils';
import {
  ContextWithStringAmount,
  GetStoredTxResponseEvent,
  GeneralErrorEvent,
  XStateEvent,
} from 'common/models/xstate';
import { MintApi } from 'sdk/mintApi';
import { CryptoUtils, GAS_SETTINGS } from 'utils/cryptoUtils';
import { InputUtils } from 'utils/inputUtils';
import { TransactionUtils } from 'utils/transactionUtils';
import { assign, interpret, spawn, Machine } from 'xstate';
import { MintEvent } from './mintEvent';
import { XStateUtils } from './xStateUtils';
import {
  createTxMachine,
  initialTxContext,
  RemoteEventExecutingTx,
  RemoteEventNoStoredTx,
  RemoteEventNoWalletConnected,
  RemoteEventTxError,
  RemoteEventTxSuccess,
  RemoteTxEvent,
} from './txMachine';
import { SwalUtils } from 'utils/swalUtils';
import { ChainId } from 'constants/constants';
import { NumberUtils } from 'utils/numberUtils';
import { Address } from 'viem';
import { MINTABLE_TOKENS } from 'constants/contracts';

export enum MintState {
  BASE_STATE = 'BASE_STATE',
  MINT_AND_GET_TX = 'MINT_AND_GET_TX',
  HANDLE_MINT_TX = 'HANDLE_MINT_TX',
  CHECK_INPUT = 'CHECK_INPUT',
  LOADING_DATA = 'LOADING_DATA',

  // Member
  RESET_MEMBER_AND_GET_TX = 'RESET_MEMBER_AND_GET_TX',
  HANDLE_RESET_MEMBER_TX = 'HANDLE_RESET_MEMBER_TX',
  APPROVE_MEMBER_AND_GET_TX = 'APPROVE_MEMBER_AND_GET_TX',
  HANDLE_APPROVE_MEMBER_TX = 'HANDLE_APPROVE_MEMBER_TX',
  CHECKING_FOR_EXISTING_TXS = 'CHECKING_FOR_EXISTING_TXS',

  // Collateral
  APPROVE_COLLATERAL_AND_GET_TX = 'APPROVE_COLLATERAL_AND_GET_TX',
  HANDLE_APPROVE_COLLATERAL_TX = 'HANDLE_APPROVE_COLLATERAL_TX',
  RESET_COLLATERAL_AND_GET_TX = 'RESET_COLLATERAL_AND_GET_TX',
  HANDLE_RESET_COLLATERAL_TX = 'HANDLE_RESET_COLLATERAL_TX',

  // Error States
  INSUFFICIENT_MEMBER = 'INSUFFICIENT_MEMBER',
  INSUFFICIENT_COLLATERAL = 'INSUFFICIENT_COLLATERAL',
  INVALID_INPUT = 'INVALID_INPUT',
  INPUT_TOO_SMALL = 'INPUT_TOO_SMALL',
  TOO_MANY_DECIMALS = 'TOO_MANY_DECIMALS',
  NO_WALLET_CONNECTED = 'NO_WALLET_CONNECTED',
}

enum MintAction {
  MINT_ERROR = 'MINT_ERROR',
  APPROVE_MEMBER_ERROR = 'APPROVE_MEMBER_ERROR',
  APPROVE_COLLATERAL_ERROR = 'APPROVE_COLLATERAL_ERROR',
  RESET_MEMBER_ERROR = 'RESET_MEMBER_ERROR',
  RESET_COLLATERAL_ERROR = 'RESET_COLLATERAL_ERROR',
}

export enum MintService {
  MINT = 'MINT',
  GET_MINT_DATA = 'getMintData',
  APPROVE_MEMBER_ALLOWANCE = 'APPROVE_MEMBER_ALLOWANCE ',
  RESET_MEMBER_ALLOWANCE = 'RESET_MEMBER_ALLOWANCE',
  APPROVE_COLLATERAL_ALLOWANCE = 'APPROVE_COLLATERAL_ALLOWANCE',
  RESET_COLLATERAL_ALLOWANCE = 'RESET_COLLATERAL_ALLOWANCE',
}

type MintSchema = {
  states: {
    [s in MintState]: object;
  };
};

export type EventInputChangeArgs = {
  inputValue: string;
};

export type EventUpdateAccountInfoArgs = {
  address: Address;
  chainId: number;
};

export type EventSelectTokenToMintArgs = {
  tokenToMint: Nullable<Address>;
  tradeUrl: Nullable<string>;
  mintingRatio: number;
  mintingFee: number;
};

// NOTE that xstate always whats the value to be named 'value', don't change the name,
// The actual value can be anything, but the variable has to be called value
type EventInputChange = XStateEvent<
  MintEvent.INPUT_CHANGE,
  EventInputChangeArgs
>;
type EventUpdateAccountInfo = XStateEvent<
  MintEvent.UPDATE_ACCOUNT_INFO,
  EventUpdateAccountInfoArgs
>;
type EventCheckInput = XStateEvent<MintEvent.CHECK_INPUT>;
type EventMint = XStateEvent<MintEvent.MINT>;
type EventSelectTokenToMint = XStateEvent<
  MintEvent.SELECT_TOKEN_TO_MINT,
  EventSelectTokenToMintArgs
>;
type EventResetMemberAllowance = XStateEvent<MintEvent.RESET_MEMBER_ALLOWANCE>;
type EventResetCollateralAllowance =
  XStateEvent<MintEvent.RESET_COLLATERAL_ALLOWANCE>;
type EventApproveMemberAllowance =
  XStateEvent<MintEvent.APPROVE_MEMBER_ALLOWANCE>;
type EventApproveCollateralAllowance =
  XStateEvent<MintEvent.APPROVE_COLLATERAL_ALLOWANCE>;

type MintEvents =
  | EventInputChange
  | EventCheckInput
  | EventMint
  | EventSelectTokenToMint
  | EventResetMemberAllowance
  | EventResetCollateralAllowance
  | EventApproveMemberAllowance
  | EventApproveCollateralAllowance
  | EventUpdateAccountInfo
  // Remote Tx Events
  | RemoteEventExecutingTx
  | RemoteEventTxError
  | RemoteEventTxSuccess
  | RemoteEventNoWalletConnected
  | RemoteEventNoStoredTx;

export type MintContext = {
  accountAddress: Nullable<Address>;
  chainId: number;
  amount: string;
  tokenToMint: Nullable<Address>; // token address
  tradeUrl: Nullable<string>; // token address
  storedTx: Nullable<StorageUtils.StoredTx>;
  inputError?: string;
  mintingRatio: number;
  mintingFee: number;
  gasLimit: bigint;
  isMinting: Nullable<boolean>;
  isLoadingData: boolean;
  mintError: Nullable<Error>;
  dataResponse: Nullable<MintApi.GetMintDataResponse>;
  dataError: Nullable<Error>;
  isMemberApproved: Nullable<boolean>;
  isCollateralApproved: Nullable<boolean>;
  isApprovingMember: boolean;
  isResettingMember: boolean;
  isApprovingCollateral: boolean;
  isResettingCollateral: boolean;
  isMintingDisabled: boolean;
  txRef: any;
} & ContextWithStringAmount;

export const initialMintContext: MintContext = {
  accountAddress: null,
  chainId: ChainId.Mainnet,
  amount: '100',
  tokenToMint: null,
  tradeUrl: null,
  storedTx: null,
  inputError: undefined,
  mintingRatio: 1,
  mintingFee: 0,
  gasLimit: CryptoUtils.calculateGasMargin(
    BigInt(GAS_SETTINGS.mint),
    ChainId.Mainnet
  ),
  isMinting: false,
  isLoadingData: false,
  mintError: null,
  dataResponse: null,
  dataError: null,
  isMemberApproved: null,
  isCollateralApproved: null,
  isApprovingMember: false,
  isResettingMember: false,
  isApprovingCollateral: false,
  isResettingCollateral: false,
  isMintingDisabled: false,
  txRef: null,
};

const isInsufficientCollateral = (context: MintContext) => {
  if (!context.dataResponse || !context.dataResponse.collateralTokenBN) {
    return true; // true being insufficient collateral
  }
  return !context.dataResponse.isSufficientCollateral;
};

const isInsufficientMember = (context: MintContext) => {
  if (!context.dataResponse || !context.dataResponse.memberTokenBN) {
    return true; // true being insufficient collateral
  }
  return !context.dataResponse.isSufficientMemberToken;
};

type GetMintDataResponseEvent = {
  type: string;
  data: MintApi.GetMintDataResponse;
};

const mintApi = MintApi.OneTokenMint.getInstance();

export const mintMachine = Machine<MintContext, MintSchema, MintEvents>(
  {
    key: 'mint',
    initial: MintState.BASE_STATE,
    context: XStateUtils.shortStringify(initialMintContext),
    states: {
      [MintState.BASE_STATE]: {
        on: {
          [MintEvent.INPUT_CHANGE]: {
            target: MintState.LOADING_DATA,
            actions: assign({
              amount: (_, event) => event.value.inputValue,
            }),
          },
          [MintEvent.UPDATE_ACCOUNT_INFO]: {
            target: MintState.BASE_STATE,
            actions: assign({
              accountAddress: (_, event: EventUpdateAccountInfo) =>
                event.value.address,
              chainId: (_, event: EventUpdateAccountInfo) =>
                event.value.chainId,
            }),
          },
          [MintEvent.SELECT_TOKEN_TO_MINT]: {
            target: MintState.LOADING_DATA,
            actions: assign({
              tokenToMint: (_, event: EventSelectTokenToMint) =>
                event.value.tokenToMint,
              tradeUrl: (_, event: EventSelectTokenToMint) =>
                event.value.tradeUrl,
              mintingRatio: (_, event: EventSelectTokenToMint) =>
                event.value.mintingRatio,
              mintingFee: (_, event: EventSelectTokenToMint) =>
                event.value.mintingFee,
            }),
          },
          [MintEvent.MINT]: {
            target: MintState.MINT_AND_GET_TX,
          },
          [MintEvent.RESET_MEMBER_ALLOWANCE]: {
            target: MintState.RESET_MEMBER_AND_GET_TX,
          },
          [MintEvent.RESET_COLLATERAL_ALLOWANCE]: {
            target: MintState.RESET_COLLATERAL_AND_GET_TX,
          },
          [MintEvent.APPROVE_MEMBER_ALLOWANCE]: {
            target: MintState.APPROVE_MEMBER_AND_GET_TX,
          },
          [MintEvent.APPROVE_COLLATERAL_ALLOWANCE]: {
            target: MintState.APPROVE_COLLATERAL_AND_GET_TX,
          },
        },
        // Continually check for any txs
        // activities: [MintActivity.CHECK_FOR_TXS]
      },
      // NOTE here I'm really assuming the only possible tx is minting, so if we want to support more types
      // in the future, would need to rework this some.
      [MintState.CHECKING_FOR_EXISTING_TXS]: {
        invoke: [
          {
            src: (context) => {
              return TransactionUtils.getStoredTx({
                accountAddress: context.accountAddress as string,
                chainId: context.chainId,
                functionName: MintApi.OneTokenMint.mintFN,
              });
            },
            // Success means there is a mint tx, re-execute mint
            onDone: {
              target: MintState.HANDLE_MINT_TX,
              actions: assign({
                storedTx: (_, event: GetStoredTxResponseEvent) => event.data,
              }),
            },
            // Error just means no tx found, go back to waiting for input
            onError: {
              target: MintState.CHECK_INPUT,
            },
          },
        ],
      },
      [MintState.MINT_AND_GET_TX]: {
        entry: assign({
          isMinting: () => true,
        }),
        exit: assign({
          isMinting: () => false,
        }),
        invoke: [
          {
            src: MintService.MINT,
            onDone: {
              // Here we aren't going to indicate success as this is 'detached' async response where
              // we have to wait for the actual tx to finish, which is being tracked in localstorage/indexDB
              // See the activities below
              target: MintState.CHECKING_FOR_EXISTING_TXS,
              actions: assign({
                storedTx: (_, event: GetStoredTxResponseEvent) => event.data,
              }),
            },
            onError: {
              target: MintState.BASE_STATE,
              actions: [
                MintAction.MINT_ERROR,
                assign({
                  mintError: (_, event: GeneralErrorEvent) => event.data,
                }),
              ],
            },
          },
        ],
      },
      [MintState.HANDLE_MINT_TX]: {
        entry: assign({
          isMinting: () => true,
          txRef: (context) =>
            spawn(
              createTxMachine('txMachine', {
                ...initialTxContext,
                chainId: (context as unknown as MintContext).chainId,
                accountAddress: (context as unknown as MintContext)
                  .accountAddress,
                storedTx: (context as unknown as MintContext).storedTx,
              }),
              {
                sync: true,
                name: `tx-machine-${
                  (context as unknown as MintContext).storedTx?.hash
                }`,
              }
            ),
        }),
        exit: assign({
          isMinting: () => false,
        }),
        // These assume the txMachine handles the notifications
        on: {
          [RemoteTxEvent.REMOTE_EXECUTING_TX]: {},
          [RemoteTxEvent.REMOTE_NO_STORED_TX]: {
            target: MintState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_NO_WALLET_CONNECTED]: {
            target: MintState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_ERROR]: {
            target: MintState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_SUCCESS]: {
            // re-'call' the action so the react component can listen and react
            actions: RemoteTxEvent.REMOTE_TX_SUCCESS,
            target: MintState.LOADING_DATA,
          },
        },
      },
      [MintState.RESET_MEMBER_AND_GET_TX]: {
        entry: assign({
          isResettingMember: () => true,
        }),
        exit: assign({
          isResettingMember: () => false,
        }),
        invoke: {
          src: MintService.RESET_MEMBER_ALLOWANCE,
          onDone: {
            target: MintState.HANDLE_RESET_MEMBER_TX,
            actions: assign({
              storedTx: (_, event: GetStoredTxResponseEvent) => event.data,
            }),
          },
          onError: {
            target: MintState.BASE_STATE,
            actions: [
              MintAction.RESET_MEMBER_ERROR,
              assign({
                isMemberApproved: (context) => context.isMemberApproved,
              }),
            ],
          },
        },
      },
      [MintState.HANDLE_RESET_MEMBER_TX]: {
        entry: assign({
          isApprovingMember: () => true,
          txRef: (context) => {
            // Can't cast above, not sure why atm
            const c = context as unknown as MintContext;
            return spawn(
              createTxMachine('txMachine', {
                ...initialTxContext,
                chainId: c.chainId,
                accountAddress: c.accountAddress,
                storedTx: c.storedTx,
              }),
              { sync: true, name: `tx-machine-${c.storedTx?.hash}` }
            );
          },
        }),
        exit: assign({
          isApprovingMember: () => false,
        }),
        // These assume the txMachine handles the notifications
        on: {
          [RemoteTxEvent.REMOTE_EXECUTING_TX]: {},
          [RemoteTxEvent.REMOTE_NO_STORED_TX]: {
            target: MintState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_NO_WALLET_CONNECTED]: {
            target: MintState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_ERROR]: {
            target: MintState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_SUCCESS]: {
            // re-'call' the action so the react component can listen and react
            actions: [RemoteTxEvent.REMOTE_TX_SUCCESS],
            target: MintState.LOADING_DATA,
          },
        },
      },
      [MintState.RESET_COLLATERAL_AND_GET_TX]: {
        entry: assign({
          isResettingCollateral: () => true,
        }),
        exit: assign({
          isResettingCollateral: () => false,
        }),
        invoke: {
          src: MintService.RESET_MEMBER_ALLOWANCE,
          onDone: {
            target: MintState.HANDLE_RESET_COLLATERAL_TX,
            actions: assign({
              storedTx: (_, event: GetStoredTxResponseEvent) => event.data,
            }),
          },
          onError: {
            target: MintState.BASE_STATE,
            actions: [MintAction.RESET_COLLATERAL_ERROR],
          },
        },
      },
      [MintState.HANDLE_RESET_COLLATERAL_TX]: {
        entry: assign({
          isResettingCollateral: () => true,
          txRef: (context) => {
            // Can't cast above, not sure why atm
            const c = context as unknown as MintContext;
            return spawn(
              createTxMachine('txMachine', {
                ...initialTxContext,
                chainId: c.chainId,
                accountAddress: c.accountAddress,
                storedTx: c.storedTx,
              }),
              { sync: true, name: `tx-machine-${c.storedTx?.hash}` }
            );
          },
        }),
        exit: assign({
          isResettingCollateral: () => false,
        }),
        // These assume the txMachine handles the notifications
        on: {
          [RemoteTxEvent.REMOTE_EXECUTING_TX]: {},
          [RemoteTxEvent.REMOTE_NO_STORED_TX]: {
            target: MintState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_NO_WALLET_CONNECTED]: {
            target: MintState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_ERROR]: {
            target: MintState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_SUCCESS]: {
            // re-'call' the action so the react component can listen and react
            actions: [RemoteTxEvent.REMOTE_TX_SUCCESS],
            target: MintState.LOADING_DATA,
          },
        },
      },
      [MintState.APPROVE_MEMBER_AND_GET_TX]: {
        entry: assign({
          isApprovingMember: () => true,
        }),
        exit: assign({
          isApprovingMember: () => false,
        }),
        invoke: [
          {
            src: MintService.APPROVE_MEMBER_ALLOWANCE,
            onDone: {
              target: MintState.HANDLE_APPROVE_MEMBER_TX,
              actions: assign({
                storedTx: (_, event: GetStoredTxResponseEvent) => event.data,
              }),
            },
            onError: {
              target: MintState.BASE_STATE,
              actions: [MintAction.APPROVE_MEMBER_ERROR],
            },
          },
        ],
      },
      [MintState.HANDLE_APPROVE_MEMBER_TX]: {
        entry: assign({
          isApprovingMember: () => true,
          txRef: (context) =>
            spawn(
              createTxMachine('txMachine', {
                ...initialTxContext,
                chainId: (context as unknown as MintContext).chainId,
                accountAddress: (context as unknown as MintContext)
                  .accountAddress,
                storedTx: (context as unknown as MintContext).storedTx,
              }),
              {
                sync: true,
                name: `tx-machine-${
                  (context as unknown as MintContext).storedTx?.hash
                }`,
              }
            ),
        }),
        exit: assign({
          isApprovingMember: () => false,
        }),
        // These assume the txMachine handles the notifications
        on: {
          [RemoteTxEvent.REMOTE_EXECUTING_TX]: {},
          [RemoteTxEvent.REMOTE_NO_STORED_TX]: {
            target: MintState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_NO_WALLET_CONNECTED]: {
            target: MintState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_ERROR]: {
            target: MintState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_SUCCESS]: {
            // re-'call' the action so the react component can listen and react
            actions: RemoteTxEvent.REMOTE_TX_SUCCESS,
            target: MintState.LOADING_DATA,
          },
        },
      },
      [MintState.APPROVE_COLLATERAL_AND_GET_TX]: {
        entry: assign({
          isApprovingCollateral: () => true,
        }),
        exit: assign({
          isApprovingCollateral: () => false,
        }),
        invoke: [
          {
            src: MintService.APPROVE_COLLATERAL_ALLOWANCE,
            onDone: {
              target: MintState.HANDLE_APPROVE_COLLATERAL_TX,
              actions: assign({
                storedTx: (_, event: GetStoredTxResponseEvent) => event.data,
              }),
            },
            onError: {
              target: MintState.BASE_STATE,
              actions: [MintAction.APPROVE_MEMBER_ERROR],
            },
          },
        ],
      },
      [MintState.HANDLE_APPROVE_COLLATERAL_TX]: {
        entry: assign({
          isApprovingCollateral: () => true,
          txRef: (context) =>
            spawn(
              createTxMachine('txMachine', {
                ...initialTxContext,
                chainId: (context as unknown as MintContext).chainId,
                accountAddress: (context as unknown as MintContext)
                  .accountAddress,
                storedTx: (context as unknown as MintContext).storedTx,
              }),
              {
                sync: true,
                name: `tx-machine-${
                  (context as unknown as MintContext).storedTx?.hash
                }`,
              }
            ),
        }),
        exit: assign({
          isApprovingCollateral: () => false,
        }),
        // These assume the txMachine handles the notifications
        on: {
          [RemoteTxEvent.REMOTE_EXECUTING_TX]: {},
          [RemoteTxEvent.REMOTE_NO_STORED_TX]: {
            target: MintState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_NO_WALLET_CONNECTED]: {
            target: MintState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_ERROR]: {
            target: MintState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_SUCCESS]: {
            // re-'call' the action so the react component can listen and react
            actions: RemoteTxEvent.REMOTE_TX_SUCCESS,
            target: MintState.LOADING_DATA,
          },
        },
      },
      [MintState.LOADING_DATA]: {
        entry: assign({
          isLoadingData: () => true,
        }),
        exit: assign({
          isLoadingData: () => false,
        }),
        invoke: [
          {
            src: MintService.GET_MINT_DATA,
            onDone: {
              target: MintState.CHECKING_FOR_EXISTING_TXS,
              actions: assign({
                dataResponse: (_, event: GetMintDataResponseEvent) =>
                  event.data,
                isMemberApproved: (_, event: GetMintDataResponseEvent) =>
                  event.data.isMemberApproved,
                isCollateralApproved: (_, event: GetMintDataResponseEvent) =>
                  event.data.isCollateralApproved,
              }),
            },
            onError: {
              target: MintState.CHECK_INPUT,
              actions: assign({
                dataError: (_, event: GeneralErrorEvent) => event.data,
              }),
            },
          },
        ],
      },
      [MintState.CHECK_INPUT]: {
        // This exit here is a great way to set context when the state itself is exiting so whatever is assigned
        // has access to the latest context.
        invoke: [],
        exit: assign({
          isMintingDisabled: (context) => {
            return (
              context.isMinting ||
              context.isLoadingData ||
              InputUtils.isDefaultInput(context) ||
              InputUtils.isZeroInput(context) ||
              InputUtils.isInputTooSmall(context) ||
              InputUtils.isInvalidInput(context) ||
              InputUtils.isNullChainId(context) ||
              InputUtils.isTooManyDecimals18(context) ||
              isInsufficientCollateral(context) ||
              isInsufficientMember(context) ||
              context.inputError != null
            );
          },
        }),
        always: [
          {
            target: MintState.BASE_STATE,
            cond: InputUtils.isDefaultInput,
          },
          {
            target: MintState.BASE_STATE,
            cond: InputUtils.isZeroInput,
          },
          {
            target: MintState.NO_WALLET_CONNECTED,
            cond: InputUtils.isNotConnected,
          },
          {
            target: MintState.INPUT_TOO_SMALL,
            cond: InputUtils.isInputTooSmall,
          },
          {
            target: MintState.TOO_MANY_DECIMALS,
            cond: InputUtils.isTooManyDecimals18,
          },
          {
            target: MintState.INSUFFICIENT_COLLATERAL,
            cond: isInsufficientCollateral,
          },
          {
            target: MintState.INSUFFICIENT_MEMBER,
            cond: isInsufficientMember,
          },
          {
            target: MintState.INVALID_INPUT,
            cond: InputUtils.isInvalidInput,
          },
          {
            target: MintState.BASE_STATE,
            cond: InputUtils.isValidInput,
          },
        ],
      },
      [MintState.INVALID_INPUT]: {
        entry: assign({
          inputError: () => 'Invalid Input!',
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [MintEvent.INPUT_CHANGE]: {
            target: MintState.LOADING_DATA,
            actions: assign({
              amount: (_, event) => event.value.inputValue,
            }),
          },
          [MintEvent.SELECT_TOKEN_TO_MINT]: {
            target: MintState.LOADING_DATA,
            actions: assign({
              mintingRatio: (_, event: EventSelectTokenToMint) =>
                event.value.mintingRatio,
              tokenToMint: (_, event: EventSelectTokenToMint) =>
                event.value.tokenToMint,
              tradeUrl: (_, event: EventSelectTokenToMint) =>
                event.value.tradeUrl,
              mintingFee: (_, event: EventSelectTokenToMint) =>
                event.value.mintingFee,
            }),
          },
          [MintEvent.UPDATE_ACCOUNT_INFO]: {
            target: MintState.BASE_STATE,
            actions: assign({
              accountAddress: (_, event: EventUpdateAccountInfo) =>
                event.value.address,
              chainId: (_, event: EventUpdateAccountInfo) =>
                event.value.chainId,
            }),
          },
        },
      },
      [MintState.INPUT_TOO_SMALL]: {
        entry: assign({
          inputError: () => 'Amount is too small!',
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [MintEvent.INPUT_CHANGE]: {
            target: MintState.LOADING_DATA,
            actions: assign({
              amount: (_, event) => event.value.inputValue,
            }),
          },
          [MintEvent.SELECT_TOKEN_TO_MINT]: {
            target: MintState.LOADING_DATA,
            actions: assign({
              mintingRatio: (_, event: EventSelectTokenToMint) =>
                event.value.mintingRatio,
              tokenToMint: (_, event: EventSelectTokenToMint) =>
                event.value.tokenToMint,
              tradeUrl: (_, event: EventSelectTokenToMint) =>
                event.value.tradeUrl,
              mintingFee: (_, event: EventSelectTokenToMint) =>
                event.value.mintingFee,
            }),
          },
          [MintEvent.UPDATE_ACCOUNT_INFO]: {
            target: MintState.CHECK_INPUT,
            actions: assign({
              accountAddress: (_, event: EventUpdateAccountInfo) =>
                event.value.address,
              chainId: (_, event: EventUpdateAccountInfo) =>
                event.value.chainId,
            }),
          },
        },
      },
      [MintState.TOO_MANY_DECIMALS]: {
        entry: assign({
          inputError: () => 'Too many decimals!',
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [MintEvent.INPUT_CHANGE]: {
            target: MintState.LOADING_DATA,
            actions: assign({
              amount: (_, event) => event.value.inputValue,
            }),
          },
          [MintEvent.SELECT_TOKEN_TO_MINT]: {
            target: MintState.LOADING_DATA,
            actions: assign({
              mintingRatio: (_, event: EventSelectTokenToMint) =>
                event.value.mintingRatio,
              tokenToMint: (_, event: EventSelectTokenToMint) =>
                event.value.tokenToMint,
              tradeUrl: (_, event: EventSelectTokenToMint) =>
                event.value.tradeUrl,
              mintingFee: (_, event: EventSelectTokenToMint) =>
                event.value.mintingFee,
            }),
          },
          [MintEvent.UPDATE_ACCOUNT_INFO]: {
            target: MintState.BASE_STATE,
            actions: assign({
              accountAddress: (_, event: EventUpdateAccountInfo) =>
                event.value.address,
              chainId: (_, event: EventUpdateAccountInfo) =>
                event.value.chainId,
            }),
          },
        },
      },
      [MintState.INSUFFICIENT_COLLATERAL]: {
        entry: assign({
          inputError: (context) => {
            const mintableTokens =
              Object.values(MINTABLE_TOKENS[context.chainId]) ?? [];
            const selectedOneToken = mintableTokens.find(
              (t) => t.token.address === context.tokenToMint
            );
            if (selectedOneToken) {
              const amountCollateral = context.dataResponse?.collateralTokenBN
                ? NumberUtils.BNtoNumberWithoutDecimals(
                    context.dataResponse?.collateralTokenBN.toString(),
                    18
                  ).toFixed(3)
                : 0;
              const errorMessage = `You need ${amountCollateral} ${selectedOneToken?.collateralToken?.name} to mint ${context.amount} ${selectedOneToken.token.name}`;
              return errorMessage;
            }
            return 'Insufficient Collateral!';
          },
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [MintEvent.INPUT_CHANGE]: {
            target: MintState.LOADING_DATA,
            actions: assign({
              amount: (_, event) => event.value.inputValue,
            }),
          },
          [MintEvent.SELECT_TOKEN_TO_MINT]: {
            target: MintState.LOADING_DATA,
            actions: assign({
              mintingRatio: (_, event: EventSelectTokenToMint) =>
                event.value.mintingRatio,
              tokenToMint: (_, event: EventSelectTokenToMint) =>
                event.value.tokenToMint,
              tradeUrl: (_, event: EventSelectTokenToMint) =>
                event.value.tradeUrl,
              mintingFee: (_, event: EventSelectTokenToMint) =>
                event.value.mintingFee,
            }),
          },
          [MintEvent.UPDATE_ACCOUNT_INFO]: {
            target: MintState.BASE_STATE,
            actions: assign({
              accountAddress: (_, event: EventUpdateAccountInfo) =>
                event.value.address,
              chainId: (_, event: EventUpdateAccountInfo) =>
                event.value.chainId,
            }),
          },
        },
      },
      [MintState.NO_WALLET_CONNECTED]: {
        entry: assign({
          inputError: () => 'Please connect a wallet to Mint!',
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [MintEvent.INPUT_CHANGE]: {
            target: MintState.LOADING_DATA,
            actions: assign({
              amount: (_, event) => event.value.inputValue,
            }),
          },
          [MintEvent.SELECT_TOKEN_TO_MINT]: {
            target: MintState.LOADING_DATA,
            actions: assign({
              mintingRatio: (_, event: EventSelectTokenToMint) =>
                event.value.mintingRatio,
              tokenToMint: (_, event: EventSelectTokenToMint) =>
                event.value.tokenToMint,
              tradeUrl: (_, event: EventSelectTokenToMint) =>
                event.value.tradeUrl,
              mintingFee: (_, event: EventSelectTokenToMint) =>
                event.value.mintingFee,
            }),
          },
          [MintEvent.UPDATE_ACCOUNT_INFO]: {
            target: MintState.BASE_STATE,
            actions: assign({
              accountAddress: (_, event: EventUpdateAccountInfo) =>
                event.value.address,
              chainId: (_, event: EventUpdateAccountInfo) =>
                event.value.chainId,
            }),
          },
        },
      },
      [MintState.INSUFFICIENT_MEMBER]: {
        entry: assign({
          inputError: (context) => {
            const mintableTokens =
              Object.values(MINTABLE_TOKENS[context.chainId]) ?? [];
            const selectedOneToken = mintableTokens.find(
              (t) => t.token.address === context.tokenToMint
            );
            if (
              context.dataResponse &&
              selectedOneToken &&
              selectedOneToken.memberToken
            ) {
              const amountTokens =
                context.dataResponse.memberTokenString &&
                selectedOneToken.memberToken.decimals
                  ? NumberUtils.significantCeil(
                      context.dataResponse.memberTokenString,
                      selectedOneToken.memberToken.decimals
                    )
                  : 0;
              const errorMessage = InputUtils.isTruthy(context.tradeUrl)
                ? `You need ${amountTokens} ${selectedOneToken.memberToken.name} to mint ${context.amount} ${selectedOneToken.token.name}. Click here to swap for it.`
                : `You need ${amountTokens} ${selectedOneToken.memberToken.name} to mint ${context.amount} ${selectedOneToken.token.name}`;
              return errorMessage;
            }
            return 'Insufficient Member Token amount!';
          },
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [MintEvent.INPUT_CHANGE]: {
            target: MintState.LOADING_DATA,
            actions: assign({
              amount: (_, event) => event.value.inputValue,
            }),
          },
          [MintEvent.SELECT_TOKEN_TO_MINT]: {
            target: MintState.LOADING_DATA,
            actions: assign({
              mintingRatio: (_, event: EventSelectTokenToMint) =>
                event.value.mintingRatio,
              tokenToMint: (_, event: EventSelectTokenToMint) =>
                event.value.tokenToMint,
              tradeUrl: (_, event: EventSelectTokenToMint) =>
                event.value.tradeUrl,
              mintingFee: (_, event: EventSelectTokenToMint) =>
                event.value.mintingFee,
            }),
          },
          [MintEvent.UPDATE_ACCOUNT_INFO]: {
            target: MintState.BASE_STATE,
            actions: assign({
              accountAddress: (_, event: EventUpdateAccountInfo) =>
                event.value.address,
              chainId: (_, event: EventUpdateAccountInfo) =>
                event.value.chainId,
            }),
          },
        },
      },
    },
  },
  {
    services: {
      [MintService.MINT]: (context) => {
        return mintApi.mint({
          chainId: context.chainId,
          accountAddress: context.accountAddress as Address,
          amount: context.amount,
          tokenToMint: context.tokenToMint,
          mintingRatio: context.mintingRatio,
          mintingFee: context.mintingFee,
        });
      },
      [MintService.GET_MINT_DATA]: (context) => {
        return mintApi.getMintData({
          accountAddress: context.accountAddress as Address,
          chainId: context.chainId,
          amount: context.amount,
          mintingRatio: context.mintingRatio,
          mintingFee: context.mintingFee,
          tokenToMint: context.tokenToMint,
        });
      },
      [MintService.APPROVE_MEMBER_ALLOWANCE]: (context) => {
        return mintApi.approveMemberAllowance({
          accountAddress: context.accountAddress as Address,
          tokenToMint: context.tokenToMint,
          chainId: context.chainId,
        });
      },
      [MintService.RESET_MEMBER_ALLOWANCE]: (context) => {
        return mintApi.resetMemberAllowance({
          accountAddress: context.accountAddress as Address,
          tokenToMint: context.tokenToMint,
          chainId: context.chainId,
        });
      },
      [MintService.APPROVE_COLLATERAL_ALLOWANCE]: (context) => {
        return mintApi.approveCollateralAllowance({
          accountAddress: context.accountAddress as Address,
          tokenToMint: context.tokenToMint,
          chainId: context.chainId,
        });
      },
      [MintService.RESET_COLLATERAL_ALLOWANCE]: (context) => {
        return mintApi.resetCollateralAllowance({
          accountAddress: context.accountAddress as Address,
          tokenToMint: context.tokenToMint,
          chainId: context.chainId,
        });
      },
    },
    actions: {
      [MintAction.MINT_ERROR]: (_, event) => {
        const evt = event as unknown as GeneralErrorEvent;
        void SwalUtils.ToastErrorTx.fire({
          title:
            evt?.data?.shortMessage ?? evt?.data?.message ?? 'Error Minting!',
        });
      },
      [MintAction.APPROVE_MEMBER_ERROR]: (_, event) => {
        const evt = event as unknown as GeneralErrorEvent;
        void SwalUtils.ToastErrorTx.fire({
          title:
            evt?.data?.shortMessage ?? evt?.data?.message ?? 'Error Approving!',
        });
      },
      [MintAction.APPROVE_COLLATERAL_ERROR]: (_, event) => {
        const evt = event as unknown as GeneralErrorEvent;
        void SwalUtils.ToastErrorTx.fire({
          title:
            evt?.data?.shortMessage ?? evt?.data?.message ?? 'Error Approving!',
        });
      },
      [MintAction.RESET_MEMBER_ERROR]: (_, event) => {
        const evt = event as unknown as GeneralErrorEvent;
        void SwalUtils.ToastErrorTx.fire({
          title:
            evt?.data?.shortMessage ??
            evt?.data?.message ??
            'Error Resetting Member!',
        });
      },
      [MintAction.RESET_COLLATERAL_ERROR]: (_, event) => {
        const evt = event as unknown as GeneralErrorEvent;
        void SwalUtils.ToastErrorTx.fire({
          title:
            evt?.data?.shortMessage ??
            evt?.data?.message ??
            'Error Resetting Collateral!',
        });
      },
    },
  }
);

export const mintService = interpret(mintMachine, {
  // devTools: process.env.NODE_ENV === 'development'
}).start();

mintService.subscribe((s) => {
  if (process.env.NODE_ENV === 'development') {
    console.debug('[mintService:debug]', s);
  }
});
